<template>
  <div class="flex justify-center items-start pt-0">
    <div class="bg-gray-100 rounded-lg shadow-lg px-14 py-8 w-[800px]">
      <div class="flex flex-col gap-8 items-center">
        <!-- Header -->
        <div class="flex flex-col items-center text-center w-full">
          <h2 class="text-display-sm font-medium leading-8 text-gray-900 mb-0">STUDENT INFORMATION</h2>
          <p class="text-sm leading-5 text-gray-900">(all fields are required)</p>
        </div>

        <!-- Form Fields -->
        <div class="flex flex-col gap-2 w-full">
          <!-- Primary Disability -->
           
          <fieldset class="flex flex-col border-0 p-0 m-0 w-80" id="iod-wizard-primary-disability">
            <legend class="text-base font-bold leading-6 text-gray-900">Primary Disability</legend>
            <GbInlineMessage
              v-if="showPrimaryDisabilityError"
              class="flex items-center gap-2 text-red-300 !p-1"
              role="alert"
            >
              <span class="flex items-center">
                <span class="material-icons-round text-red-300 text-lg pr-2" aria-hidden="true">warning_amber</span>
                <span>Please select a primary disability</span>
              </span>
            </GbInlineMessage>
            <SelectGroup
              :class="showPrimaryDisabilityError ? 'error pt-0' : 'pt-2'"
              :showLabel="false"
              group-label="Primary Disability"
              placeholder="Select a disability"
              :filter-group="availableDisabilityOptions"
              v-model="selectedDisability"
              @update:model-value="updateDisability"
              gbTheme="purple"
            />

          </fieldset>

          <!-- Impacted Subjects -->
          <fieldset class="flex flex-col gap-1 w-full border-0 p-0 m-0">
            <legend class="text-base font-bold leading-6 text-gray-900">Impacted Subjects</legend>
            <GbInlineMessage
              v-if="showSubjectsError"
              class="flex items-center gap-2 text-red-300 !p-1"
              role="alert"
            >
              <span class="flex items-center">
                <span class="material-icons-round text-red-300 text-lg pr-2" aria-hidden="true">warning_amber</span>
                <span>Please select at least one subject area</span>
              </span>
            </GbInlineMessage>
            <div class="flex gap-16 items-center pt-2">
              <div
                v-for="subject in subjectOptions"
                :key="subject.value"
                class="flex items-center gap-3"
              >
                <Checkbox
                  :modelValue="selectedSubjects.includes(subject.value)"
                  @update:modelValue="toggleSubject(subject.value)"
                  :binary="true"
                  :inputId="`subject-${subject.value}`"
                  gbTheme="purple"
                />
                <label :for="`subject-${subject.value}`" class="text-base leading-6 text-gray-900 cursor-pointer">
                  {{ subject.label }}
                </label>
              </div>
            </div>
          </fieldset>

          <!-- Impacted Areas -->
          <fieldset class="flex flex-col gap-1 w-full border-0 p-0 mt-5 m-0" v-if="showImpactAreas">
            <legend class="text-base font-bold leading-6 text-gray-900 pb-2">Impacted Areas</legend>
            <GbInlineMessage
              v-if="showImpactAreasError"
              class="flex items-center gap-2 text-red-300 !p-1"
              role="alert"
            >
              <span class="flex items-center">
                <span class="material-icons-round text-red-300 text-lg pr-2" aria-hidden="true">warning_amber</span>
                <span>Please select at least one impact area</span>
              </span>
            </GbInlineMessage>
            <SelectionChip
              :items="impactAreaOptions"
              v-model="selectedImpactAreas"
              item-key="area_uuid"
              item-label="area_label"
              :show-icon="true"
              @update:model-value="updateImpactAreas"
            />
          </fieldset>
        </div>

        <!-- Compose Button and Reset -->
        <div class="flex gap-2 self-start pt-4">
          <GbButton
            type="button"
            gbTheme="purplePrimary"
            size="large"
            @click="composeStatement"
          >
            Compose impact of disability statement
          </GbButton>
          <GbButton
            type="button"
            gbTheme="grayOutlineSecondary"
            size="large"
            @click="reset"
            v-if="selectedDisability || selectedSubjects.length > 0 || selectedImpactAreas.length > 0"
          >
            <span class="material-icons-round text-xl">replay</span>
            Reset
          </GbButton>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import SelectionChip from '@/components/SelectionChip.vue'
import Checkbox from 'primevue/checkbox'
import SelectGroup from '@/componentlibrary/SelectGroup.vue'
import GbButton from '@/components/GbButton.vue'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { IMPACT_OF_DISABILITY_EVENT_NAMES } from './event_names'
import type { ImpactArea, DisabilityOption, SubjectOption } from './impact-of-disability_types'
import GbInlineMessage from '@/components/GbInlineMessage.vue'

interface Props {
  disability: string
  disabilityOptions: DisabilityOption[]
  loadingOptions?: boolean
  subjects: string[]
  subjectOptions: SubjectOption[]
  impactAreas: ImpactArea[]
  impactAreasByDisability: Map<string, ImpactArea[]>
}

const props = withDefaults(defineProps<Props>(), {
  loadingOptions: false
})

const emit = defineEmits<{
  'update:disability': [value: string]
  'update:subjects': [value: string[]]
  'update:impactAreas': [value: ImpactArea[]]
}>()

const router = useRouter()
const route = useRoute()
const analytics = getAnalyticsTracker()

const selectedDisability = ref(props.disability)
const selectedSubjects = ref<string[]>([...props.subjects])
const selectedImpactAreas = ref<ImpactArea[]>([...props.impactAreas])

const impactAreaOptions = computed(() => {
  if (selectedDisability.value) {
    const areas = props.impactAreasByDisability.get(selectedDisability.value) || []
    return [...areas].sort((a, b) => parseInt(a.area_order) - parseInt(b.area_order))
  }
  return []
})

const showImpactAreas = computed(() => selectedDisability.value && !props.loadingOptions && impactAreaOptions.value.length > 1)

const showPrimaryDisabilityError = ref(false)
const showSubjectsError = ref(false)
const showImpactAreasError = ref(false)

const availableDisabilityOptions = computed(() => props.disabilityOptions)

function updateDisability(newValue: string) {
  selectedDisability.value = newValue
  if (newValue) {
    showPrimaryDisabilityError.value = false
  }
  selectedImpactAreas.value = []
}

function toggleSubject(subjectValue: string) {
  if (selectedSubjects.value.includes(subjectValue)) {
    selectedSubjects.value = selectedSubjects.value.filter(s => s !== subjectValue)
  } else {
    selectedSubjects.value = [...selectedSubjects.value, subjectValue]
  }
  if (selectedSubjects.value.length > 0) {
    showSubjectsError.value = false
  }
}

function reset() {
  selectedDisability.value = ''
  selectedSubjects.value = []
  selectedImpactAreas.value = []
  showPrimaryDisabilityError.value = false
  showSubjectsError.value = false
  showImpactAreasError.value = false
  analytics.sendEvent(IMPACT_OF_DISABILITY_EVENT_NAMES.RESET_WIZARD)
}

function updateImpactAreas(newVal: ImpactArea[]) {
  selectedImpactAreas.value = newVal
  if (newVal.length > 0) {
    showImpactAreasError.value = false
  }
}

function composeStatement() {
  if (!selectedDisability.value || selectedSubjects.value.length === 0 || (showImpactAreas.value && selectedImpactAreas.value.length === 0)) {
    if (!selectedDisability.value) {
      showPrimaryDisabilityError.value = true
    }
    if (selectedSubjects.value.length === 0) {
      showSubjectsError.value = true
    }
    if (showImpactAreas.value && selectedImpactAreas.value.length === 0) {
      showImpactAreasError.value = true
    }
    return
  }
  
  const newQuery: Record<string, any> = {
    ...route.query,
    subjects: selectedSubjects.value,
    disability: selectedDisability.value,
    impactAreas: selectedImpactAreas.value.map(ia => ia.area_uuid)
  }

  analytics.sendEvent(IMPACT_OF_DISABILITY_EVENT_NAMES.CLICKED_COMPOSE)
  
  router.push({
    name: 'ImpactOfDisability',
    query: newQuery
  })
}
</script>
